// ============================================
// 2026-10-17（标绘层 S3）：箭头族几何（自研，纯函数，可单测）
//  - 直箭头 arrowStraight：2 点（尾→尖）
//  - 细直箭头 arrowFine：2 点（细干 + 后掠翼）
//  - 曲线箭头 arrowCurve：3 点（尾→控制点→尖，二次贝塞尔 + 末端箭头）
// 全部按水平面（xz）生成、y 沿轴线插值；返回闭合多边形（可直接填充 + 描边）。
// 算法为自研实现（仅语义参考通用标绘惯例），不移植 GPL 代码。
// ============================================
import type { P3 } from './primitives';

export interface ArrowStyleOpts {
  /** 头大小倍率（默认 1） */
  headSize?: number;
  /** 干宽倍率（默认 1） */
  tailWidth?: number;
}

interface DirInfo { d: [number, number]; n: [number, number]; len: number }

function horizontalDir(a: P3, b: P3): DirInfo | null {
  const dx = b[0] - a[0];
  const dz = b[2] - a[2];
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return null;
  const d: [number, number] = [dx / len, dz / len];
  return { d, n: [-d[1], d[0]], len };
}

/** 顶点 y：按在轴线上的投影比例从 tail.y 插值到 tip.y */
function yAt(tail: P3, tip: P3, x: number, z: number, d: [number, number], len: number): number {
  const t = Math.max(0, Math.min(1, ((x - tail[0]) * d[0] + (z - tail[2]) * d[1]) / (len || 1)));
  return tail[1] + (tip[1] - tail[1]) * t;
}

function degenerate(points: P3[]): P3[] {
  const a = points[0];
  return [a, a];
}

/** 直箭头（实心多边形，闭合）：尾 2 点 → 干 → 头三角 → 尖 */
export function arrowStraightPoints(tail: P3, tip: P3, opts?: ArrowStyleOpts): P3[] {
  const dir = horizontalDir(tail, tip);
  if (!dir) return degenerate([tail, tip]);
  const { d, n, len } = dir;
  const hScale = Math.max(0.1, opts?.headSize ?? 1);
  const tScale = Math.max(0.1, opts?.tailWidth ?? 1);
  const head = Math.min(Math.max(len * 0.22, 0.5), len * 0.6) * hScale;
  const halfW = head * 0.6 * hScale;
  const shaft = Math.max(0.04, head * 0.18) * tScale;
  const bx = tip[0] - d[0] * head;
  const bz = tip[2] - d[1] * head;
  const mk = (x: number, z: number): P3 => [x, yAt(tail, tip, x, z, d, len), z];
  const pts: P3[] = [
    mk(tail[0] + n[0] * shaft, tail[2] + n[1] * shaft),
    mk(bx + n[0] * shaft, bz + n[1] * shaft),
    mk(bx + n[0] * halfW, bz + n[1] * halfW),
    mk(tip[0], tip[2]),
    mk(bx - n[0] * halfW, bz - n[1] * halfW),
    mk(bx - n[0] * shaft, bz - n[1] * shaft),
    mk(tail[0] - n[0] * shaft, tail[2] - n[1] * shaft),
  ];
  return [...pts, pts[0]];
}

/** 细直箭头：细干 + 后掠翼（比直箭头更瘦） */
export function arrowFinePoints(tail: P3, tip: P3, opts?: ArrowStyleOpts): P3[] {
  const dir = horizontalDir(tail, tip);
  if (!dir) return degenerate([tail, tip]);
  const { d, n, len } = dir;
  const hScale = Math.max(0.1, opts?.headSize ?? 1);
  const tScale = Math.max(0.1, opts?.tailWidth ?? 1);
  const head = Math.min(Math.max(len * 0.18, 0.4), len * 0.5) * hScale;
  const halfW = head * 0.45 * hScale;
  const shaft = Math.max(0.03, head * 0.1) * tScale;
  const sweep = head * 0.35;
  const bx = tip[0] - d[0] * head;
  const bz = tip[2] - d[1] * head;
  const wx = bx - d[0] * sweep;
  const wz = bz - d[1] * sweep;
  const mk = (x: number, z: number): P3 => [x, yAt(tail, tip, x, z, d, len), z];
  const pts: P3[] = [
    mk(tail[0] + n[0] * shaft, tail[2] + n[1] * shaft),
    mk(bx + n[0] * shaft, bz + n[1] * shaft),
    mk(wx + n[0] * halfW, wz + n[1] * halfW),
    mk(tip[0], tip[2]),
    mk(wx - n[0] * halfW, wz - n[1] * halfW),
    mk(bx - n[0] * shaft, bz - n[1] * shaft),
    mk(tail[0] - n[0] * shaft, tail[2] - n[1] * shaft),
  ];
  return [...pts, pts[0]];
}

/** 曲线箭头：尾 → 控制点 → 尖（二次贝塞尔干线 + 末端箭头） */
export function arrowCurvePoints(tail: P3, ctrl: P3, tip: P3, opts?: ArrowStyleOpts, segments = 24): P3[] {
  const segs = Math.max(6, Math.floor(segments));
  const center: P3[] = [];
  for (let i = 0; i <= segs; i++) {
    const t = i / segs;
    const a = (1 - t) * (1 - t);
    const b = 2 * (1 - t) * t;
    const c = t * t;
    center.push([
      a * tail[0] + b * ctrl[0] + c * tip[0],
      a * tail[1] + b * ctrl[1] + c * tip[1],
      a * tail[2] + b * ctrl[2] + c * tip[2],
    ]);
  }
  let total = 0;
  for (let i = 1; i < center.length; i++) {
    total += Math.hypot(center[i][0] - center[i - 1][0], center[i][1] - center[i - 1][1], center[i][2] - center[i - 1][2]);
  }
  if (total < 1e-6) return degenerate([tail, tip]);
  const hScale = Math.max(0.1, opts?.headSize ?? 1);
  const tScale = Math.max(0.1, opts?.tailWidth ?? 1);
  const head = Math.min(Math.max(total * 0.16, 0.4), total * 0.4) * hScale;
  const halfW = head * 0.5 * hScale;
  const shaft = Math.max(0.03, head * 0.14) * tScale;
  // 干线修剪到箭头基部（距尖 > head）
  const line = center.filter((p) => Math.hypot(p[0] - tip[0], p[1] - tip[1], p[2] - tip[2]) > head * 0.9);
  if (line.length < 2) return degenerate([tail, tip]);
  const left: P3[] = [];
  const right: P3[] = [];
  for (let i = 0; i < line.length; i++) {
    const prev = line[Math.max(0, i - 1)];
    const next = line[Math.min(line.length - 1, i + 1)];
    let tx = next[0] - prev[0];
    let tz = next[2] - prev[2];
    const tl = Math.hypot(tx, tz) || 1;
    tx /= tl;
    tz /= tl;
    const nx = -tz;
    const nz = tx;
    left.push([line[i][0] + nx * shaft, line[i][1], line[i][2] + nz * shaft]);
    right.push([line[i][0] - nx * shaft, line[i][1], line[i][2] - nz * shaft]);
  }
  // 箭头方向：末端切线（tip - ctrl；退化则取最后一段）
  let tdx = tip[0] - ctrl[0];
  let tdz = tip[2] - ctrl[2];
  let tl = Math.hypot(tdx, tdz);
  if (tl < 1e-6) {
    const last = line[line.length - 1];
    const prev = line[line.length - 2];
    tdx = last[0] - prev[0];
    tdz = last[2] - prev[2];
    tl = Math.hypot(tdx, tdz) || 1;
  }
  const dEnd: [number, number] = [tdx / tl, tdz / tl];
  const nEnd: [number, number] = [-dEnd[1], dEnd[0]];
  const bx = tip[0] - dEnd[0] * head;
  const bz = tip[2] - dEnd[1] * head;
  const wingL: P3 = [bx + nEnd[0] * halfW, tip[1], bz + nEnd[1] * halfW];
  const wingR: P3 = [bx - nEnd[0] * halfW, tip[1], bz - nEnd[1] * halfW];
  const tipPt: P3 = [tip[0], tip[1], tip[2]];
  const pts: P3[] = [...left, wingL, tipPt, wingR, ...right.reverse()];
  return [...pts, pts[0]];
}
